import type { ProductoVista } from '@barbones/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/api/cliente';
import { Button } from '@/components/ui/button';
import { Campo, Input } from '@/components/ui/form';
import { Dialogo } from '@/components/ui/overlay';
import { avisarError } from '@/lib/avisos';

interface DialogoEliminarProductoProps {
  producto: ProductoVista;
  onCerrar: () => void;
  onEliminado: () => void;
}

export function DialogoEliminarProducto({ producto, onCerrar, onEliminado }: DialogoEliminarProductoProps) {
  const cliente = useQueryClient();
  const [confirmacion, setConfirmacion] = useState('');
  const coincide = confirmacion.trim() === producto.nombre;
  const eliminar = useMutation({
    mutationFn: () => api(`/productos/${producto.id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['productos'] });
      cliente.removeQueries({ queryKey: ['productos', producto.id] });
      toast.success('Producto eliminado');
      onEliminado();
    },
    onError: (error) => avisarError(error),
  });

  return (
    <Dialogo
      abierto
      ancho="sm"
      onCambiarAbierto={(abierto) => !abierto && !eliminar.isPending && onCerrar()}
      titulo="Eliminar producto"
      descripcion={producto.nombre}
      pie={
        <>
          <Button variante="secundario" onClick={onCerrar} disabled={eliminar.isPending}>
            Cancelar
          </Button>
          <Button variante="peligro" onClick={() => eliminar.mutate()} cargando={eliminar.isPending} disabled={!coincide}>
            <Trash2 /> Eliminar producto
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        <div className="flex gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-[13px]">
          <TriangleAlert className="mt-0.5 size-5 shrink-0 text-destructive" aria-hidden />
          <div>
            <p className="font-semibold text-destructive">Esta acción no se puede deshacer desde el sistema.</p>
            <p className="mt-1 text-foreground-muted">
              El producto desaparecerá del inventario, las búsquedas y la caja. El historial de compras y movimientos se conservará.
            </p>
          </div>
        </div>
        <Campo etiqueta="Nombre del producto" ayuda={`Escribe “${producto.nombre}” para confirmar.`}>
          {(props) => (
            <Input
              {...props}
              value={confirmacion}
              onChange={(evento) => setConfirmacion(evento.target.value)}
              autoComplete="off"
              autoFocus
            />
          )}
        </Campo>
      </div>
    </Dialogo>
  );
}
